'use client';

/**
 * GlassButton Component
 *
 * A button with gradient background and glow effect on hover.
 * Supports multiple variants and sizes.
 * Includes built-in security props for permission/tier/license locking.
 *
 * @see Issue #383 - Permission-Aware UI Components with Lock Icon Pattern
 */

import Button, { type ButtonProps } from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha, styled, useTheme } from '@mui/material/styles';
import type { SecurityProps } from '@ops-matrix/shared-types';
import { motion } from 'framer-motion';
import React, { forwardRef, useCallback } from 'react';
import { useSecurityLock } from '../../hooks/useSecurityLock';
import { baseColors } from '../../theme/palette';
import { interactionAnimations, transitionPresets } from '../../theme/transitions';
import { GlassTooltip } from '../GlassTooltip';
import { LockIndicator } from '../LockIndicator/LockIndicator';

// ============================================================================
// Types
// ============================================================================

export interface GlassButtonProps extends Omit<ButtonProps, 'variant' | 'color'>, SecurityProps {
  /**
   * HTML anchor target (used when `href` is set).
   * @example '_blank'
   */
  target?: React.HTMLAttributeAnchorTarget;

  /**
   * HTML anchor rel attribute (used when `href` is set).
   * @example 'noopener noreferrer'
   */
  rel?: string;
  /**
   * Button variant
   * - 'subtle': Light border, minimal style (default) - use for most buttons
   * - 'outlined': Colored border with slight emphasis
   * - 'ghost': No border, text only
   * - 'gradient': Solid gradient fill - use sparingly for primary CTAs
   * - 'glow': Solid fill with glow effect
   * @default 'subtle'
   */
  variant?: 'subtle' | 'outlined' | 'ghost' | 'gradient' | 'glow' | 'contained' | 'text' | 'neon';

  /**
   * Color of the button (supports MUI theme colors)
   * @default 'primary'
   */
  color?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';

  /**
   * Loading state
   * @default false
   */
  loading?: boolean;

  /** Text to show in place of children when loading */
  loadingText?: string;

  /**
   * Pulse animation (attention-grabbing)
   * @default false
   */
  pulse?: boolean;
}

// ============================================================================
// Styled Components
// ============================================================================

interface StyledButtonProps {
  $variant: 'subtle' | 'outlined' | 'ghost' | 'gradient' | 'glow' | 'contained' | 'text' | 'neon';
  $color: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
  $isDark: boolean;
}

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledButtonProps>(({ theme, $variant, $color, $isDark }) => {
  // Get the theme color palette for the selected color
  const colorPalette = $color === 'inherit' ? theme.palette.primary : theme.palette[$color];
  // In dark mode, MUI swaps main to pastel (e.g. #90caf9 for primary).
  // For filled buttons we want the vibrant saturated color as background.
  const vibrant = $isDark ? colorPalette.dark : colorPalette.main;
  const vibrantDark = $isDark
    ? (colorPalette as unknown as Record<string, string>)[800] || colorPalette.dark
    : colorPalette.dark;

  const isMaterial = theme.designGuideline === 'material';

  // ── Material Design 3 ──
  if (isMaterial) {
    return {
      position: 'relative',
      borderRadius: 20, // MD3 rounded rectangle
      padding: '10px 24px',
      fontWeight: 500,
      fontSize: '0.875rem', // 14px MD3 label-large
      textTransform: 'none',
      transition: transitionPresets.button,
      overflow: 'hidden',
      minHeight: 40, // MD3 standard height
      letterSpacing: '0.01em',

      // ── MD3 Tonal (subtle) — secondaryContainer surface ──
      ...($variant === 'subtle' && {
        background: $isDark ? alpha(colorPalette.main, 0.12) : alpha(colorPalette.main, 0.08),
        color: $isDark ? colorPalette.light : colorPalette.dark,
        border: 'none',
        boxShadow: 'none',

        '&:hover': {
          background: $isDark ? alpha(colorPalette.main, 0.18) : alpha(colorPalette.main, 0.14),
        },

        '&:active': {
          background: $isDark ? alpha(colorPalette.main, 0.22) : alpha(colorPalette.main, 0.18),
        },
      }),

      // ── MD3 Outlined — transparent with outline border ──
      ...($variant === 'outlined' && {
        background: 'transparent',
        color: colorPalette.main,
        border: `1px solid ${$isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`,
        boxShadow: 'none',

        '&:hover': {
          background: alpha(colorPalette.main, 0.08),
          borderColor: $isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.16)',
        },
      }),

      // ── MD3 Text (ghost) — no fill, no border ──
      ...(($variant === 'ghost' || $variant === 'text') && {
        background: 'transparent',
        color: colorPalette.main,
        border: 'none',
        boxShadow: 'none',

        '&:hover': {
          background: alpha(colorPalette.main, 0.08),
        },
      }),

      // ── MD3 Filled (gradient/contained/glow) — solid primary ──
      ...(($variant === 'gradient' || $variant === 'contained' || $variant === 'glow') && {
        '&.MuiButton-root': {
          background: vibrant,
          color: theme.palette.getContrastText(vibrant),
          border: 'none',
          fontWeight: 500,
          boxShadow: '0px 1px 2px 0px rgba(0,0,0,0.30), 0px 1px 3px 1px rgba(0,0,0,0.15)',

          '&:hover': {
            background: vibrantDark,
            boxShadow: '0px 1px 2px 0px rgba(0,0,0,0.30), 0px 2px 6px 2px rgba(0,0,0,0.15)',
          },
        },
      }),

      // ── MD3 Neon → falls back to Outlined style ──
      ...($variant === 'neon' && {
        background: 'transparent',
        color: colorPalette.main,
        border: `1px solid ${alpha(colorPalette.main, 0.5)}`,
        fontWeight: 500,
        boxShadow: 'none',

        '&:hover': {
          background: alpha(colorPalette.main, 0.08),
          borderColor: colorPalette.main,
        },
      }),

      // Disabled state
      '&.Mui-disabled': {
        opacity: 0.38, // MD3 disabled opacity
        cursor: 'not-allowed',
      },

      // Size variants
      '&.MuiButton-sizeSmall': {
        padding: '6px 16px',
        fontSize: '0.8125rem',
        borderRadius: 20,
        minHeight: 32,
      },

      '&.MuiButton-sizeLarge': {
        padding: '12px 32px',
        fontSize: '0.9375rem',
        borderRadius: 20,
        minHeight: 48, // MD3 large touch target
      },
    };
  }

  // ── Apple Liquid Glass (original) ──
  return {
    position: 'relative',
    borderRadius: 9999, // Capsule/pill — Apple Liquid Glass default
    padding: '8px 20px',
    fontWeight: 600,
    fontSize: '0.9375rem', // 15 pt Apple body
    textTransform: 'none',
    transition: transitionPresets.button,
    overflow: 'hidden',
    minHeight: 36,

    // ── Apple Liquid Glass: specular highlight (top-edge shine) ──
    // Applied to every interactive variant except ghost / text
    ...($variant !== 'ghost' &&
      $variant !== 'text' && {
        '&::before': {
          content: '""',
          position: 'absolute' as const,
          top: 0,
          left: 0,
          right: 0,
          height: '40%',
          background: `linear-gradient(180deg, ${
            $isDark ? 'rgba(255,255,255,0.09)' : 'rgba(255,255,255,0.18)'
          } 0%, transparent 100%)`,
          borderRadius: 'inherit',
          pointerEvents: 'none' as const,
          zIndex: 1,
        },
      }),

    // ── Subtle (default) — Pure frosted glass, no accent colour ──
    ...($variant === 'subtle' && {
      background: $isDark ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.55)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      color: $isDark ? baseColors.neutral[300] : baseColors.neutral[700],
      border: `0.5px solid ${$isDark ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.72)'}`,
      boxShadow: $isDark
        ? '0 1px 4px rgba(0,0,0,0.35), inset 0 0.5px 0 rgba(255,255,255,0.06)'
        : '0 1px 4px rgba(0,0,0,0.06), 0 0.5px 1.5px rgba(0,0,0,0.04)',

      '&:hover': {
        background: $isDark ? 'rgba(255,255,255,0.11)' : 'rgba(255,255,255,0.68)',
        borderColor: $isDark ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.85)',
        boxShadow: $isDark
          ? '0 2px 8px rgba(0,0,0,0.45), inset 0 0.5px 0 rgba(255,255,255,0.08)'
          : '0 2px 8px rgba(0,0,0,0.09), 0 1px 3px rgba(0,0,0,0.05)',
      },

      '&:active': {
        background: $isDark ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.75)',
      },
    }),

    // ── Outlined — Frosted glass with accent-coloured border ──
    ...($variant === 'outlined' && {
      background: $isDark ? alpha(colorPalette.main, 0.06) : alpha(colorPalette.main, 0.05),
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      color: colorPalette.main,
      border: `1px solid ${alpha(colorPalette.main, 0.3)}`,
      boxShadow: $isDark ? '0 1px 4px rgba(0,0,0,0.25)' : '0 1px 4px rgba(0,0,0,0.05)',

      '&:hover': {
        background: $isDark ? alpha(colorPalette.main, 0.12) : alpha(colorPalette.main, 0.1),
        borderColor: alpha(colorPalette.main, 0.5),
        boxShadow: $isDark
          ? `0 2px 8px rgba(0,0,0,0.35), 0 0 0 1px ${alpha(colorPalette.main, 0.08)}`
          : `0 2px 8px rgba(0,0,0,0.08), 0 0 0 1px ${alpha(colorPalette.main, 0.05)}`,
      },
    }),

    // ── Ghost / Text — No glass; glass materialises on hover ──
    ...(($variant === 'ghost' || $variant === 'text') && {
      background: 'transparent',
      color: $isDark ? 'rgba(255,255,255,0.85)' : baseColors.neutral[700],
      border: 'none',
      boxShadow: 'none',

      '&:hover': {
        background: $isDark ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.50)',
        backdropFilter: 'blur(16px) saturate(150%)',
        WebkitBackdropFilter: 'blur(16px) saturate(150%)',
        borderRadius: 9999,
        boxShadow: $isDark
          ? 'inset 0 0.5px 0 rgba(255,255,255,0.06)'
          : '0 1px 3px rgba(0,0,0,0.04), inset 0 0.5px 0 rgba(255,255,255,0.55)',
      },
    }),

    // ── Gradient — Glass CTA with accent gradient tint ──
    ...($variant === 'gradient' && {
      '&.MuiButton-root': {
        background: $isDark
          ? `linear-gradient(135deg, ${alpha(vibrant, 0.55)} 0%, ${alpha(vibrantDark, 0.72)} 100%)`
          : `linear-gradient(135deg, ${vibrant} 0%, ${vibrantDark} 100%)`,
        backdropFilter: $isDark ? 'blur(20px) saturate(180%)' : 'none',
        WebkitBackdropFilter: $isDark ? 'blur(20px) saturate(180%)' : 'none',
        color: '#fff',
        border: `0.5px solid ${$isDark ? alpha(vibrant, 0.2) : alpha('#fff', 0.25)}`,
        fontWeight: 600,
        boxShadow: `0 2px 10px ${alpha(vibrant, 0.22)}, 0 1px 4px rgba(0,0,0,0.08)`,

        '&:hover': {
          background: $isDark
            ? `linear-gradient(135deg, ${alpha(vibrant, 0.65)} 0%, ${alpha(vibrantDark, 0.82)} 100%)`
            : `linear-gradient(135deg, ${vibrantDark} 0%, ${vibrant} 100%)`,
          boxShadow: `0 4px 20px ${alpha(vibrant, 0.32)}, 0 2px 8px rgba(0,0,0,0.10)`,
        },
      },
    }),

    // ── Neon — Frosted glass with neon accent glow border ──
    ...($variant === 'neon' && {
      background: $isDark ? 'rgba(28,28,30,0.30)' : 'rgba(255,255,255,0.50)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      color: colorPalette.main,
      border: `${$isDark ? '0.5px' : '1.5px'} solid ${alpha(colorPalette.main, $isDark ? 0.35 : 0.45)}`,
      fontWeight: 600,
      boxShadow: $isDark
        ? `0 0 8px ${alpha(colorPalette.main, 0.12)}, 0 1px 4px rgba(0,0,0,0.06)`
        : '0 1px 3px rgba(0,0,0,0.08)',
      textShadow: $isDark ? `0 0 10px ${alpha(colorPalette.main, 0.2)}` : 'none',

      '&:hover': {
        background: $isDark ? 'rgba(28,28,30,0.38)' : 'rgba(255,255,255,0.60)',
        borderColor: alpha(colorPalette.main, 0.55),
        boxShadow: $isDark
          ? `0 0 14px ${alpha(colorPalette.main, 0.22)}, 0 0 28px ${alpha(colorPalette.main, 0.08)}, 0 2px 8px rgba(0,0,0,0.08)`
          : '0 2px 8px rgba(0,0,0,0.12)',
        textShadow: $isDark ? `0 0 14px ${alpha(colorPalette.main, 0.35)}` : 'none',
      },
    }),

    // ── Glow / Contained — Accent-tinted glass with glow ──
    ...(($variant === 'glow' || $variant === 'contained') && {
      '&.MuiButton-root': {
        background: alpha(vibrant, 0.5),
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        color: '#fff',
        border: `0.5px solid ${$isDark ? alpha(vibrant, 0.2) : 'rgba(255,255,255,0.30)'}`,
        fontWeight: 600,
        boxShadow: `0 0 8px ${alpha(vibrant, 0.18)}, 0 1px 4px rgba(0,0,0,0.08)`,

        '&:hover': {
          background: alpha(vibrant, 0.62),
          boxShadow: `0 0 16px ${alpha(vibrant, 0.28)}, 0 2px 8px rgba(0,0,0,0.10)`,
        },
      },
    }),

    // Disabled state
    '&.Mui-disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },

    // Size variants — all capsule
    '&.MuiButton-sizeSmall': {
      padding: '6px 14px',
      fontSize: '0.8125rem', // 13 pt
      borderRadius: 9999,
      minHeight: 28,
    },

    '&.MuiButton-sizeLarge': {
      padding: '10px 28px',
      fontSize: '1.0625rem', // 17 pt
      borderRadius: 9999,
      minHeight: 44, // Apple standard touch target
    },
  };
});

// Pulse animation wrapper
const PulseWrapper = styled(motion.div)({
  display: 'inline-flex',
});

// ============================================================================
// Component
// ============================================================================

export const GlassButton = forwardRef<HTMLButtonElement, GlassButtonProps>(
  (
    {
      children,
      variant = 'subtle',
      color = 'primary',
      loading = false,
      loadingText,
      pulse = false,
      disabled,
      startIcon,
      endIcon,
      onClick,
      // Security props
      permission,
      anyPermission,
      allPermissions,
      requiredTier,
      requiredLicense,
      anyLicense,
      showLockIcon = true,
      showTierBadge = true,
      onLockedClick,
      lockedTooltip,
      ...props
    },
    ref,
  ) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    // Security lock state
    const { lockState, isLocked, handleLockedClick, lockedTooltipText } = useSecurityLock({
      permission,
      anyPermission,
      allPermissions,
      requiredTier,
      requiredLicense,
      anyLicense,
      onLockedClick,
      lockedTooltip,
    });

    const isDisabled = disabled || loading || isLocked;

    // Handle click with security interception
    const handleClick = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        if (isLocked) {
          e.preventDefault();
          e.stopPropagation();
          handleLockedClick();
          return;
        }
        onClick?.(e);
      },
      [isLocked, handleLockedClick, onClick],
    );

    // Compute end icon (lock icon if locked)
    const computedEndIcon =
      isLocked && showLockIcon && lockState ? (
        <LockIndicator lockState={lockState} size="small" showTierBadge={showTierBadge} showTooltip={false} />
      ) : (
        endIcon
      );

    const buttonContent = (
      <StyledButton
        ref={ref}
        $variant={variant}
        $color={color}
        $isDark={isDark}
        disabled={isDisabled}
        startIcon={loading ? <CircularProgress size={18} color="inherit" sx={{ opacity: 0.8 }} /> : startIcon}
        endIcon={computedEndIcon}
        onClick={handleClick}
        {...props}
      >
        {loading && loadingText ? loadingText : children}
      </StyledButton>
    );

    // Wrap with tooltip if locked
    const wrappedButton = isLocked ? (
      <GlassTooltip title={lockedTooltipText} placement="top">
        {buttonContent}
      </GlassTooltip>
    ) : (
      buttonContent
    );

    if (pulse && !isDisabled) {
      return (
        <PulseWrapper
          animate={{
            scale: [1, 1.02, 1],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          whileHover={interactionAnimations.buttonHover}
          whileTap={interactionAnimations.buttonTap}
        >
          {wrappedButton}
        </PulseWrapper>
      );
    }

    return (
      <motion.div
        style={{ display: 'inline-flex' }}
        whileHover={isDisabled ? undefined : interactionAnimations.buttonHover}
        whileTap={isDisabled ? undefined : interactionAnimations.buttonTap}
      >
        {wrappedButton}
      </motion.div>
    );
  },
);

GlassButton.displayName = 'GlassButton';

export default GlassButton;
